import { useEffect, useState, type JSX } from "react";
import { toast } from "sonner";
import {
  SHOPPING_CATEGORIES,
  type ShoppingCategory,
  type ShoppingListItem,
  type PatchShoppingListItemRequest,
} from "@recipevault/shared";
import { useSync } from "../../sync/useSync.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import { Label } from "@/ui/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select.js";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/ui/sheet.js";
import { CATEGORY_LABELS } from "./categoryLabels.js";

interface EditItemSheetProps {
  item: ShoppingListItem | null;
  listId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Long-press-to-edit sheet for a shopping list item (Plan 14 Task 14). Replaces
 * the old inline tap-to-edit form — same fields, same `patchItemOptimistic` /
 * `deleteItemOptimistic` optimistic calls, now in a bottom sheet reachable via
 * long-press on a `StoreModeRow` (active lists) or a tap on an archived row.
 */
export function EditItemSheet({
  item,
  listId,
  open,
  onOpenChange,
}: EditItemSheetProps): JSX.Element {
  const { service } = useSync();
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("");
  const [category, setCategory] = useState<ShoppingCategory>("other");

  // Seed the form from `item` whenever a new item is opened.
  useEffect(() => {
    if (!open || !item) return;
    setName(item.name);
    setQty(item.qty != null ? String(item.qty) : "");
    setUnit(item.unit ?? "");
    setCategory(item.category);
  }, [item, open]);

  async function handleSave(): Promise<void> {
    if (!item) return;
    const trimmedName = name.trim();
    if (!trimmedName) return;
    const parsedQty = qty.trim() === "" ? null : Number(qty);
    const trimmedUnit = unit.trim();
    const body: PatchShoppingListItemRequest = {
      name: trimmedName,
      qty: parsedQty != null && Number.isFinite(parsedQty) ? parsedQty : null,
      unit: trimmedUnit === "" ? null : trimmedUnit,
      category,
    };
    onOpenChange(false);
    try {
      await service.patchItemOptimistic(listId, item.id, body);
    } catch {
      toast.error("Couldn't save item — please try again");
    }
  }

  async function handleRemove(): Promise<void> {
    if (!item) return;
    const itemId = item.id;
    onOpenChange(false);
    try {
      await service.deleteItemOptimistic(listId, itemId);
    } catch {
      toast.error("Couldn't remove item — please try again");
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="pb-safe">
        <SheetHeader>
          <SheetTitle>Edit item</SheetTitle>
        </SheetHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
          className="flex flex-col gap-3 px-4 pb-4"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-item-name">Item name</Label>
            <Input
              id="edit-item-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              aria-label="Item name"
              maxLength={200}
              autoFocus
            />
          </div>

          <div className="flex gap-2">
            <div className="flex w-20 flex-col gap-1.5">
              <Label htmlFor="edit-item-qty">Qty</Label>
              <Input
                id="edit-item-qty"
                value={qty}
                onChange={(e) => {
                  setQty(e.target.value);
                }}
                placeholder="Qty"
                aria-label="Item quantity"
                inputMode="decimal"
              />
            </div>
            <div className="flex w-24 flex-col gap-1.5">
              <Label htmlFor="edit-item-unit">Unit</Label>
              <Input
                id="edit-item-unit"
                value={unit}
                onChange={(e) => {
                  setUnit(e.target.value);
                }}
                placeholder="Unit"
                aria-label="Item unit"
                maxLength={32}
              />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label>Category</Label>
              <Select
                value={category}
                onValueChange={(v) => {
                  setCategory(v as ShoppingCategory);
                }}
              >
                <SelectTrigger className="w-full" aria-label="Item category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SHOPPING_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {CATEGORY_LABELS[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="cook"
              className="flex-1"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" size="cook" className="flex-1" disabled={!name.trim()}>
              Save
            </Button>
          </div>
          <Button
            type="button"
            variant="destructive"
            size="cook"
            onClick={() => void handleRemove()}
          >
            Remove
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
